import {Box, Typography} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {useState} from 'react'
import type {FC, ReactElement} from 'react'
import ViewAsModal from './ViewAsModal'
import {useCurrentUser} from '@shared/hooks/useCurrentUser'
import {useViewAs} from '@shared/hooks/useViewAs'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'

/**
 * Red "Viewing as …" banner shown in the AppBar while a "View As" session is
 * active. Clicking it opens a popover listing the impersonated teams + roles and
 * a Reset Session action. Renders nothing when not viewing as anyone.
 */
const ViewAsBanner: FC = (): ReactElement | null => {
  const [t] = useCustomTranslation('common')
  const {viewAs} = useCurrentUser()
  const {resetViewAs, isResetting} = useViewAs()
  const [open, setOpen] = useState(false)

  if (!viewAs.active) {
    return null
  }

  const teamNames = (viewAs.teams ?? []).map((team) => team.name).join(', ')

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setOpen(true)
          }
        }}
        data-testid="mbai-view-as-banner"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          // Breathing room between the global search input and the banner.
          ml: {xs: 1, sm: 3},
          px: 1.5,
          py: 0.5,
          cursor: 'pointer',
          border: 1,
          borderColor: 'error.main',
          borderRadius: 1,
          color: 'error.main',
          maxWidth: {xs: 160, sm: 320},
        }}
      >
        <VisibilityIcon fontSize="small" />
        <Typography variant="body2" noWrap sx={{fontWeight: 500}}>
          {t('view_as.banner', {names: teamNames})}
        </Typography>
      </Box>

      <ViewAsModal
        open={open}
        onClose={() => setOpen(false)}
        viewAs={viewAs}
        onReset={resetViewAs}
        isResetting={isResetting}
      />
    </>
  )
}

export default ViewAsBanner
